import type { ChartDimensions, GroupedMetric, ScaleGroup } from '../../types';
import { CHART_CONSTANTS } from '../../constants';

interface StandardLegendProps {
  mode: 'standard';
  dimensions: ChartDimensions;
  height: number;
}

interface MetricLegendProps {
  mode: 'metric';
  dimensions: ChartDimensions;
  allGroupedMetrics: GroupedMetric[];
  hasDualAxis: boolean;
  leftAxisGroup?: ScaleGroup;
  rightAxisGroup?: ScaleGroup;
}

type ChartLegendProps = StandardLegendProps | MetricLegendProps;

/**
 * Renders chart legend.
 * Supports standard comparison mode and metric selection mode with dual-axis.
 */
export function ChartLegend(props: ChartLegendProps) {
  const { dimensions } = props;
  const { chartWidth, paddingTop, paddingRight, paddingLeft } = dimensions;
  const { COLORS } = CHART_CONSTANTS;

  if (props.mode === 'standard') {
    const { height } = props;

    return (
      <>
        {/* Current period indicator (blue) */}
        <g>
          <rect
            x={chartWidth - 45}
            y={height - 42}
            width={10}
            height={10}
            fill={COLORS.CURRENT}
            rx="2"
          />
          <text
            x={chartWidth - 30}
            y={height - 33}
            fontSize="9"
            fill={COLORS.CURRENT}
            fontWeight="500"
          >
            Atual
          </text>
        </g>
        {/* Comparison period indicator (blue) */}
        <g>
          <rect
            x={chartWidth - 45}
            y={height - 25}
            width={10}
            height={10}
            fill={COLORS.COMPARISON}
            opacity="0.6"
            rx="2"
          />
          <text
            x={chartWidth - 30}
            y={height - 16}
            fontSize="9"
            fill={COLORS.COMPARISON}
          >
            Anterior
          </text>
        </g>
      </>
    );
  }

  // Metric mode
  const { allGroupedMetrics, hasDualAxis, leftAxisGroup, rightAxisGroup } = props;

  if (!hasDualAxis) {
    // Single axis - legend on right side
    return (
      <>
        {allGroupedMetrics.map((metric, idx) => (
          <g key={metric.key}>
            <rect
              x={chartWidth - 110}
              y={paddingTop + idx * 18}
              width={10}
              height={10}
              fill={metric.color}
              rx="2"
            />
            <text
              x={chartWidth - 95}
              y={paddingTop + idx * 18 + 9}
              fontSize="9"
              fill="#ffffff"
              fontWeight="500"
            >
              {metric.label}
            </text>
          </g>
        ))}
      </>
    );
  }

  // Dual axis - legends on both sides
  return (
    <>
      {/* Left axis group legend */}
      {leftAxisGroup && leftAxisGroup.metrics.map((metric, idx) => (
        <g key={metric.key}>
          <rect
            x={paddingLeft + 10}
            y={paddingTop + idx * 18}
            width={10}
            height={10}
            fill={metric.color}
            rx="2"
          />
          <text
            x={paddingLeft + 25}
            y={paddingTop + idx * 18 + 9}
            fontSize="9"
            fill="#ffffff"
            fontWeight="500"
          >
            {metric.label} ◀
          </text>
        </g>
      ))}
      {/* Right axis group legend */}
      {rightAxisGroup && rightAxisGroup.metrics.map((metric, idx) => (
        <g key={metric.key}>
          <rect
            x={chartWidth - paddingRight - 90}
            y={paddingTop + idx * 18}
            width={10}
            height={10}
            fill={metric.color}
            rx="2"
          />
          <text
            x={chartWidth - paddingRight - 75}
            y={paddingTop + idx * 18 + 9}
            fontSize="9"
            fill="#ffffff"
            fontWeight="500"
          >
            ▶ {metric.label}
          </text>
        </g>
      ))}
    </>
  );
}
